import { type ReactNode } from 'react'

import { cn } from '#app/utils/misc.tsx'

export type EmptyStateProps = {
	/**
	 * Glyph shown in the ringed icon disc above (default) or beside (compact) the
	 * copy. Optional — a bare empty state renders title + description only.
	 */
	icon?: ReactNode
	title: ReactNode
	/** Muted supporting line; capped at ~36ch so it stays a readable measure. */
	description?: ReactNode
	/**
	 * A call-to-action — the recorded slot takes a `Button variant="outline"
	 * size="sm"`. The container itself is non-interactive; only what you pass
	 * here is.
	 */
	action?: ReactNode
	/**
	 * `default` = centered column for a whole-surface zero-state (tables, feeds);
	 * `compact` = horizontal row for an in-list zero-state (SettingsRow lists).
	 * @default 'default'
	 */
	variant?: 'default' | 'compact'
	className?: string
}

/** The ringed icon disc — `--gray-2` fill, `--gray-4` ring, muted glyph. */
function IconDisc({
	children,
	className,
}: {
	children: ReactNode
	className?: string
}) {
	return (
		<div
			data-slot="empty-state-icon"
			className={cn(
				'text-muted-foreground flex shrink-0 items-center justify-center rounded-full border border-[var(--gray-4)] bg-[var(--gray-2)]',
				className,
			)}
		>
			{children}
		</div>
	)
}

/**
 * Per-variant class divergences. `default` centers a column for a whole-surface
 * zero-state; `compact` lays the same parts out as a horizontal in-list row. The
 * shared skeleton (disc + title + description + action) is rendered once below —
 * only these tokens differ between the two.
 */
const variantStyles = {
	default: {
		root: 'flex-col items-center gap-3.5 px-6 py-12 text-center',
		disc: 'size-11',
		copy: 'gap-1',
		description: 'text-body-xs mx-auto max-w-[36ch] text-pretty',
		action: 'mt-1',
	},
	compact: {
		root: 'flex-row items-center gap-3 text-left',
		disc: 'size-8',
		copy: 'gap-0.5',
		description: 'text-body-2xs',
		action: 'ml-auto shrink-0',
	},
} as const

/**
 * The one canonical empty-state grammar: a ringed icon disc + title + optional
 * description and action. `default` centers the column for a whole-surface
 * zero-state (Table renders its `emptyState` through it); `compact` lays the
 * same parts out as a horizontal in-list row. The container is non-interactive —
 * only the `action` you pass is focusable. Tokens only.
 */
function EmptyState({
	icon,
	title,
	description,
	action,
	variant = 'default',
	className,
}: EmptyStateProps) {
	const styles = variantStyles[variant]
	return (
		<div
			data-slot="empty-state"
			data-variant={variant}
			className={cn('flex', styles.root, className)}
		>
			{icon != null ? <IconDisc className={styles.disc}>{icon}</IconDisc> : null}
			<div className={cn('flex min-w-0 flex-col', styles.copy)}>
				<p className="text-body-xs font-semibold">{title}</p>
				{description != null ? (
					<p className={cn('text-muted-foreground', styles.description)}>
						{description}
					</p>
				) : null}
			</div>
			{action != null ? <div className={styles.action}>{action}</div> : null}
		</div>
	)
}

export { EmptyState }
